/** US and CA market keys for per-market latest trade dates. */
export const SUPPORTED_MARKETS = ['US', 'CA'];

/**
 * Align raw `market` from API/Athena with screener keys US | CA.
 */
export const normalizeStockMarket = (marketRaw) => {
  const m = (marketRaw || '').toString().trim().toUpperCase();
  if (!m) return '';
  if (m === 'US' || m === 'USA' || m.includes('UNITED STATES')) return 'US';
  if (m === 'CA' || m === 'CAN' || m.includes('CANADA')) return 'CA';
  return m;
};

/** Infer US/CA from symbol_id when row metadata is missing (e.g. FOO_TSX). */
export const inferMarketFromSymbolId = (symbolId) => {
  if (!symbolId) return null;
  const upper = String(symbolId).toUpperCase();
  if (/_TSX$|_TSXV$|_CSE$|_NEO$/.test(upper)) return 'CA';
  if (/_TO$|\.TO$/.test(upper)) return 'CA';
  return null;
};

/** Infer US/CA from exchange code when market column is empty. */
export const inferMarketFromExchange = (exchange) => {
  const ex = (exchange || '').toString().trim().toUpperCase();
  if (!ex) return null;
  if (ex === 'TSX' || ex === 'TSXV' || ex === 'CSE' || ex === 'NEO' || ex.includes('TORONTO')) return 'CA';
  return null;
};

export const resolveMarketForStock = (stock) => {
  if (!stock) return 'US';
  const normalized = normalizeStockMarket(stock.market);
  if (normalized === 'US' || normalized === 'CA') return normalized;
  const fromExchange = inferMarketFromExchange(stock.exchange);
  if (fromExchange) return fromExchange;
  const fromId = inferMarketFromSymbolId(stock.symbol_id);
  if (fromId) return fromId;
  return 'US';
};

export const getTradeDateForMarket = (latestByMarket, market) => {
  if (!latestByMarket) return null;
  const key = normalizeStockMarket(market) || 'US';
  if (latestByMarket[key]) return latestByMarket[key];
  if (key !== 'US' && latestByMarket.US) return latestByMarket.US;
  if (latestByMarket.CA) return latestByMarket.CA;
  return null;
};

/**
 * Latest trade date for screener market selector: US, CA, or max(US, CA) for All Markets.
 * @param {Record<string, string|null>} latestByMarket
 * @param {string} marketKey - '' | 'US' | 'CA'
 * @returns {string|null} YYYY-MM-DD
 */
export const getLatestTradeDateForScreenerMarket = (latestByMarket, marketKey) => {
  if (!latestByMarket) return null;
  if (marketKey === 'CA') return latestByMarket.CA || null;
  if (marketKey === 'US') return latestByMarket.US || null;
  const dates = [latestByMarket.US, latestByMarket.CA].filter(Boolean);
  if (dates.length === 0) return null;
  return dates.reduce((latest, d) => (d > latest ? d : latest));
};

/** Current calendar date in America/New_York (YYYY-MM-DD). */
export const getCurrentNyTradeDateYmd = () =>
  new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });

/** Normalize a date-like value to YYYY-MM-DD, or '' if not a calendar date. */
export const ymdFromDateLike = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const ymd = raw.slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(ymd) ? ymd : '';
};

/**
 * Most recent listed trade date strictly before `beforeYmd`.
 * Combined US+CA list — used when a market's previous session is not stored separately.
 */
export const previousListedTradeDate = (tradeDates, beforeYmd) => {
  const before = ymdFromDateLike(beforeYmd);
  if (!before) return '';
  let prev = '';
  for (const d of tradeDates || []) {
    const ymd = ymdFromDateLike(d);
    if (ymd && ymd < before && ymd > prev) prev = ymd;
  }
  return prev;
};

/**
 * DESC-sorted YYYY-MM-DD list (trade_dates / news pub_dates): true when newest equals today (NY).
 * When true, list-metadata fetches can stop — the calendar has caught up to the current session.
 */
export const isLatestDateListAtToday = (dates) => {
  if (!Array.isArray(dates) || dates.length === 0) return false;
  const latest = String(dates[0]).trim().slice(0, 10);
  return latest === getCurrentNyTradeDateYmd();
};

/**
 * Screener live quotes: only when selected trade date equals today's NY calendar date.
 */
export const shouldApplyScreenerLiveQuotes = (selectedYmd) => {
  if (!selectedYmd) return false;
  return selectedYmd === getCurrentNyTradeDateYmd();
};

export const uniqueTradeDatesFromLatestByMarket = (latestByMarket) => {
  if (!latestByMarket) return [];
  const dates = SUPPORTED_MARKETS.map((m) => latestByMarket[m]).filter(Boolean);
  return [...new Set(dates)];
};

export const serializeLatestByMarket = (latestByMarket) => {
  const us = (latestByMarket && latestByMarket.US) || '';
  const ca = (latestByMarket && latestByMarket.CA) || '';
  return `US:${us}|CA:${ca}`;
};

export const ensureLatestByMarket = (partial, tradeDatesDesc = []) => {
  const out = { US: partial?.US || null, CA: partial?.CA || null };
  const fallback = tradeDatesDesc && tradeDatesDesc.length > 0 ? tradeDatesDesc[0] : null;
  // Only backfill US from the combined trade_dates list. CA has its own holiday calendar
  // and must come from per-market Athena/API — never copy the global list head to CA.
  if (!out.US) out.US = fallback;
  return out;
};

/**
 * True when cached per-market US latest is behind the combined trade_dates list head
 * (e.g. US session loaded for today but cache still has yesterday).
 */
export const isPerMarketLatestStale = (latestByMarket, tradeDatesDesc) => {
  if (!latestByMarket?.US || !Array.isArray(tradeDatesDesc) || tradeDatesDesc.length === 0) {
    return false;
  }
  const head = String(tradeDatesDesc[0]).trim().slice(0, 10);
  const us = String(latestByMarket.US).trim().slice(0, 10);
  return Boolean(head && us && head > us);
};
